<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { QuizType } from "@/enums/quiz.enums";
import { KickoffMasteryTrackingEvents } from '~/enums/tracking/kickoff-mastery.enums';
import { ActivityEvents } from '~/enums/tracking/tracking-events.enums';
import { CmeProviders } from "~/enums/cme-providers.enums";

const {
  course,
  isWorkshop,
  isCourse,
  getQuiz,
  getChapterByActivity,
  getChapterIndexByActivity,
} = useCourseState();
const {
  quiz,
  startQuiz,
  onQuizStarted,
} = useQuizStore();
const { t } = useI18n();
const route = useRoute();

const isKickoffQuiz = computed(() => quiz.value?.type === QuizType.Kickoff);
const isMasteryQuiz = computed(() => quiz.value?.type === QuizType.Mastery);
const chapter = computed(() => getChapterByActivity(route.params.quizId as string));
const quizType = computed(() => isCourse.value ? 'quiz' : 'case');

const { dispatchTrackingEvent } = useTracking();
const { dispatchCmsEvent } = useCmsEventTracking();

onQuizStarted((data: {quizId: number; quizType: string | undefined}) => {
  if (data.quizType === QuizType.Kickoff) {
    dispatchTrackingEvent(KickoffMasteryTrackingEvents.KickoffStarted);
  }

  let eventType = CmsEventTypes.QuizStarted;
  switch (data.quizType) {
    case QuizType.Kickoff:
      eventType = CmsEventTypes.KickoffQuizStarted;
      break;
    case QuizType.Mastery:
      eventType = CmsEventTypes.MasteryQuizStarted;
      break;
  }

  dispatchCmsEvent(eventType, data.quizId);
  trackQuizStarted();
});

const trackQuizStarted = () => {
  if (!quiz.value || !chapter.value || !course.value) {
    return;
  }

  let activityType: ActivityEvents;

  switch (quiz.value.type) {
    case QuizType.Kickoff:
      activityType = ActivityEvents.KickoffQuizStarted;
      break;
    case QuizType.Mastery:
      activityType = ActivityEvents.MasteryQuizStarted;
      break;
    default:
      activityType = ActivityEvents.QuizStarted;
  }

  dispatchTrackingEvent(activityType, {
    quiz_name: quiz.value.title,
    quiz_id: quiz.value.quizId,
    chapter_name: chapter.value.title,
    course_name: course.value.courseTitle,
    course_id: course.value.id,
    type: course.value.type,
    duration: quiz.value.questions.length || 0,
  });
};

// fixme: This is a workaround for missing information on the quiz api.
const quizDetails = computed(() => {
  return getQuiz(route.params.quizId as string);
});

const requireAcknowledgmentOfAllLessonsWatched = computed(() => {
  if (isKickoffQuiz.value || isMasteryQuiz.value) {
    return false;
  }

  if (course.value?.cmeProviderSlug === CmeProviders.CmeOutfitters) {
    return !chapter.value?.contents
      .filter(content => content.type === "lesson")
      .every(content => content.completed);
  }

  return false;
});

const hasAcknowledgedWatchingAllLessons = ref(false);
</script>

<template>
  <div class="w-full h-full flex justify-center items-center pb-[env(safe-area-inset-bottom)]">
    <div
      :class="[
        'h-full w-full max-w-[800px]',
        'flex md:flex-row flex-col-reverse sm-h:flex-row items-center justify-between',
        'md:gap-16'
      ]"
    >
      <div
        :class="[
          'relative z-[1] text-left',
          'md:h-auto sm-h:h-auto h-full',
          'flex flex-col gap-2',
          'px-4 md:pt-0 pt-4 md-h:pt-0 md:pb-0 pb-4'
        ]"
      >
        <template v-if="isKickoffQuiz || isMasteryQuiz">
          <div>
            <Tag
              :value="isKickoffQuiz ? t('course.quiz.kickoff.title') : isMasteryQuiz ? t('course.quiz.mastery.title') : ''"
              class="uppercase"
              style="background: #785AA3"
            />
          </div>

          <div class="sm:text-28 text-22 font-bold text-neutral-80 my-3 md-h:my-1">
            <template v-if="isKickoffQuiz">
              {{t('course.quiz.kickoff.subtitle')}}
            </template>
            <template v-if="isMasteryQuiz">
              {{t('course.quiz.mastery.subtitle')}}
            </template>
          </div>

          <div class="text-16 text-neutral-80">
            <template v-if="isKickoffQuiz">
              {{t('course.quiz.kickoff.description')}}
            </template>
            <template v-if="isMasteryQuiz">
              {{t('course.quiz.mastery.description')}}
            </template>
          </div>
        </template>

        <template v-else>
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold">
            {{t(`course.quiz.regular.${quizType}.title`, {index: (getChapterIndexByActivity(route.params.quizId as string) || 0) + 1})}}
          </div>

          <div class="text-22 font-bold text-neutral-90">
            {{quizDetails?.title}}
          </div>

          <div
            v-if="isCourse"
            v-sanitize-html="t('course.quiz.regular.quiz.description', {
              count: quiz?.questions.length,
              passPercentage: '70%'
            }).replace('70%', '<strong>' + '70%' + '<strong/>')"
          />

          <template v-if="isWorkshop">
            <div v-sanitize-html="chapter?.description" />

            <div
              v-sanitize-html="t('course.quiz.regular.case.description', {
                count: quiz?.questions.length,
                passPercentage: 'all questions'
              }).replace('all questions', '<strong>' + 'all questions' + '<strong/>')"
            />
          </template>
        </template>

        <div class="h-full" />

        <div class="flex flex-col gap-4 md-h:mt-4 md:mt-10">
          <label
            v-if="requireAcknowledgmentOfAllLessonsWatched"
            class="flex gap-2"
          >
            <Checkbox
              v-model="hasAcknowledgedWatchingAllLessons"
              binary
            />

            <span>
              {{t('course.quiz.acknowledgmentOfAllLessonsWatchedLabel')}}
            </span>
          </label>

          <div class="sm-h:w-auto w-full">
            <Button
              class="rounded-full bg-blue-30 border-none sm-h:w-auto w-full px-16"
              :disabled="requireAcknowledgmentOfAllLessonsWatched && !hasAcknowledgedWatchingAllLessons"
              @click="startQuiz()"
            >
              <div class="flex items-center justify-center w-full gap-2 text-22 text-neutral-90 font-bold">
                <span>
                  <template v-if="isKickoffQuiz">
                    {{t('course.quiz.kickoff.start')}}
                  </template>
                  <template v-else-if="isMasteryQuiz">
                    {{t('course.quiz.mastery.start')}}
                  </template>
                  <template v-else>
                    {{t('course.quiz.regular.start', {type: t(`course.quiz.type.${quizType}`)})}}
                  </template>
                </span>
                <FontAwesomeIcon icon="fa-bolt" />
              </div>
            </Button>
          </div>

          <div
            v-if="!isKickoffQuiz && !isMasteryQuiz"
            class="text-neutral-50 text-14 md:text-left text-center sm-h:text-left"
          >
            {{t('course.quiz.regular.note', {type: t(`course.quiz.type.${quizType}`)})}}
          </div>
        </div>
      </div>

      <div class="sm-h:absolute sm:right-10 right-4 z-[0] flex items-center justify-center md:py-0 md-h:py-0 py-6">
        <img
          v-if="isKickoffQuiz"
          src="@/assets/images/gifs/survey-form.gif"
          class="xl:w-[350px] w-[250px] sm-h:w-[150px] aspect-square"
          alt="Kick-off quiz animation"
        >

        <img
          v-else-if="isMasteryQuiz"
          src="@/assets/images/gifs/graduation-cap.gif"
          class="xl:w-[350px] w-[250px] aspect-square"
          alt="Mastery quiz animation"
        >

        <FontAwesomeIcon
          v-else
          :icon="isWorkshop ? 'fa-stethoscope' : 'fa-bolt-lightning'"
          class="md:text-[350px] text-[250px] text-neutral-10"
        />
      </div>
    </div>
  </div>
</template>
